import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  DownloadIcon,
  RobotIcon,
  ChartLineUpIcon,
  LightningIcon,
  KeyIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import { usePageView } from "@/lib/usePageView";
import HubFooter from "./HubFooter";
import StoreBackground from "./StoreBackground";
import { RiskNotice } from "./RiskNotice";
import StoreTopbar from "./StoreTopbar";

/**
 * Setup hub — pick MT5 or cTrader, then follow numbered steps.
 * Query-param drives the active tab: /setup?platform=mt5 or /setup?platform=ctrader
 * Default: MT5 (larger audience).
 */
export default function Setup() {
  usePageView("/setup");
  // cTrader Copy hidden for the software-only launch; CtraderSteps kept for future use.
  const platform = "mt5";

  return (
    <StoreBackground theme={platform}>
      <div
        data-testid="setup-page"
        className="relative min-h-screen bg-transparent text-[#F1F5F9] overflow-x-clip"
      >
        {/* topbar */}
        <StoreTopbar subtitle="Setup guide" />

        {/* hero */}
        <section className="relative z-10 max-w-[1200px] mx-auto px-6 pt-16 pb-8">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#22D3EE]">
            Step-by-step
          </div>
          <h1 className="text-4xl md:text-5xl font-medium tracking-tight mt-3 max-w-3xl leading-[1.05]">
            Get set up in under <span className="text-[#D6FF38]">10 minutes.</span>
          </h1>
          <p className="text-base text-[#94A3B8] mt-4 max-w-2xl">
            How to install BotGenomix software in your own MetaTrader 5 terminal. You choose your broker, install the software and decide
            whether to run it. Plans are monthly - cancel any time from Manage billing and access ends at the end of the paid period.
          </p>

          <RiskNotice testid="setup-risk" className="mt-8" />
        </section>

        {/* steps */}
        <section className="relative z-10 max-w-[1200px] mx-auto px-6 pb-24">
          <Mt5Steps />
        </section>

        <HubFooter />
      </div>
    </StoreBackground>
  );
}

// ─── MT5 steps ──────────────────────────────────────────────────────────
function Mt5Steps() {
  const steps = [
    {
      n: 1,
      icon: UserCircleIcon,
      h: "Use your own MT5 account with a broker you choose",
      b: "The software works with MetaTrader 5 accounts from any broker that offers the bot's symbol. We don't recommend or select brokers, open accounts for you or receive referral commissions - choosing and maintaining your broker account is up to you.",
      tips: [
        "Works on demo and live MT5 accounts",
        "GBP, USD, or EUR base currency all fine",
        "Symbol names, spreads and trading hours differ between brokers and can change results",
      ],
    },
    {
      n: 2,
      icon: LightningIcon,
      h: "Create an account, choose a licence plan and your bots",
      b: "Create a free BotGenomix account, choose a monthly plan (1, 5 or all bots) via Stripe, then tick the bots you want on your account page. Your .ex5 downloads and personal licence key appear there instantly.",
      tips: [
        "Monthly rolling — cancel any time from Manage billing",
        "The software stops opening trades when the plan ends",
        "Each picked bot runs on up to 2 MT5 accounts (1 demo + 1 live)",
      ],
    },
    {
      n: 3,
      icon: DownloadIcon,
      h: "Drop the .ex5 file into MetaTrader 5",
      b: "First allow our licence server: Tools → Options → Expert Advisors → tick 'Allow WebRequest for listed URL' and add https://botgenomix.com. Then open File → Open Data Folder → MQL5 → Experts, copy in the .ex5 from your account page and refresh the Navigator.",
      tips: [
        "Folder path is normally: %APPDATA%\\MetaQuotes\\Terminal\\<hash>\\MQL5\\Experts\\",
        "The EA will appear in the Navigator panel under Expert Advisors",
        "License key goes into the EA's input parameters (step 5)",
      ],
    },
    {
      n: 4,
      icon: ChartLineUpIcon,
      h: "Attach the EA to the correct chart",
      b: "Open the exact symbol + timeframe the bot was built for (shown on the bot's page, e.g. US500 M15). Drag the EA from the Navigator onto that chart.",
      tips: [
        "Symbol + timeframe must match the listing - the rules were developed and tested on that chart only",
        "Each picked bot links to 1 demo + 1 live MT5 account - reset links from your account page"
      ],
    },
    {
      n: 5,
      icon: KeyIcon,
      h: "Paste your licence key and decide whether to enable AutoTrading",
      b: "In the EA's Inputs tab, paste your license key next to LicenseKey. The other inputs are the defaults used in the published backtests; changing them means results will differ from those shown. Click OK. The bot only trades if you switch on AutoTrading in the top toolbar.",
      tips: [
        "The EA checks your licence on start-up and every few hours (24h grace if our server is unreachable)",
        "If AutoTrading is red, no trades will be placed — this is MT5's kill-switch",
        "The licence check only confirms your licence - it can't change the strategy or send trading instructions",
      ],
    },
    {
      n: 6,
      icon: CheckCircleIcon,
      h: "Monitor and manage it yourself",
      b: "Your MT5 terminal must stay running (or use a VPS) for the EA to trade. Track performance via our dashboard, your MT5 journal, or your broker's app.",
      tips: [
        "Consider a £5/month Windows VPS if you want 24/7 uptime",
        "Backtests and demo results are not reliable indicators of future performance",
        "Pause anytime by disabling the EA — resume by re-enabling",
      ],
    },
  ];
  return <StepList steps={steps} tint="#38BDF8" accent="#FBBF24" />;
}

// ─── cTrader steps ──────────────────────────────────────────────────────
export function CtraderSteps() {
  const steps = [
    {
      n: 1,
      icon: UserCircleIcon,
      h: "Open a cTrader account",
      b: "Sign up with any cTrader-compatible broker — Pepperstone, IC Markets, FxPro, ICM.com, and Spotware Trader all work. You need a live account (demo accounts can't receive Copy).",
      tips: [
        "GBP, USD, EUR, or AUD base all supported",
        "Minimum recommended deposit: £250 (so allocations scale properly)",
        "Standard or Razor account types both fine",
      ],
    },
    {
      n: 2,
      icon: LightningIcon,
      h: "Subscribe on BotGenomix",
      b: "Pick your master strategy on the storefront and start your monthly subscription via Stripe. Immediately after checkout you'll get a cTrader Copy invite link.",
      tips: [
        "Monthly rolling — cancel any time",
        "Access ends at end of billing period on cancel",
        "One subscription = one cTrader account",
      ],
    },
    {
      n: 3,
      icon: ChartLineUpIcon,
      h: "Follow the master via your Copy link",
      b: "Click the link in your welcome email. It opens cTrader with the master strategy pre-loaded. Confirm the follow.",
      tips: [
        "Master runs on our VPS 24/7 — no software for you to install",
        "Every trade mirrors to your account in sub-second time",
        "Works on desktop, web, and mobile cTrader",
      ],
    },
    {
      n: 4,
      icon: KeyIcon,
      h: "Set your allocation",
      b: "In the Copy tab, set how much of your equity mirrors the master. £250 minimum, up to your full balance. cTrader auto-scales position sizes so your risk-per-trade matches the master proportionally.",
      tips: [
        "Start small — try £250 for a month before scaling up",
        "You can raise or lower allocation any time",
        "Fees are Equity fee only — no performance fee on top of your subscription",
      ],
    },
    {
      n: 5,
      icon: CheckCircleIcon,
      h: "You're done — sit back",
      b: "That's it. No EA to attach, no VPS to run. Positions open and close automatically on your account. Track everything in your cTrader Copy dashboard.",
      tips: [
        "You keep 100% control — pause or stop copying any time",
        "Monthly performance report emailed to you",
        "Withdrawals work normally — cTrader Copy doesn't lock your funds",
      ],
    },
  ];
  return <StepList steps={steps} tint="#2ECA7F" accent="#F87171" />;
}

// ─── shared step-list renderer ──────────────────────────────────────────
function StepList({ steps, tint, accent }) {
  return (
    <div className="relative">
      {/* vertical timeline */}
      <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px" style={{ background: `linear-gradient(180deg, ${tint}00, ${tint}66, ${tint}00)` }} />

      <div className="space-y-8">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.n} className="relative sm:pl-16" data-testid={`setup-step-${s.n}`}>
              {/* number bubble on timeline */}
              <div
                className="mb-3 sm:mb-0 sm:absolute sm:left-0 sm:top-0 w-12 h-12 rounded-lg border flex items-center justify-center backdrop-blur-md"
                style={{ borderColor: `${tint}66`, background: `${tint}10` }}
              >
                <Icon size={20} weight="fill" style={{ color: tint }} />
              </div>

              {/* card */}
              <div className="rounded-xl border border-[#1A1C24] bg-[#0B0E14]/70 backdrop-blur-sm p-4 sm:p-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span
                    className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{ color: tint, background: `${tint}15`, border: `1px solid ${tint}30` }}
                  >
                    Step {s.n}
                  </span>
                </div>
                <h3 className="text-xl font-medium tracking-tight">{s.h}</h3>
                <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{s.b}</p>
                {s.tips && (
                  <ul className="mt-4 space-y-1.5 text-[13px] text-[#CBD5E1]">
                    {s.tips.map((t, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: accent }} />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* final CTA */}
      <div className="mt-12 rounded-xl border p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
           style={{ borderColor: `${tint}40`, background: `${tint}08` }}>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest" style={{ color: tint }}>Need a hand?</div>
          <p className="text-sm text-[#CBD5E1] mt-1">
            Setup guide didn't cover something? Email us for technical help installing or running the software.
          </p>
        </div>
        <a
          href="mailto:botgenomix.support@gmail.com"
          data-testid="setup-contact-support"
          className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-lg border transition"
          style={{ borderColor: tint, color: tint }}
        >
          Contact support →
        </a>
      </div>
    </div>
  );
}
